import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { footerContent } from '../data/content';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white" data-testid="footer">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">AC</span>
              </div>
              <span className="font-bold text-xl">ACDC-Formation</span>
            </div>
            <p className="text-gray-400 mb-6 max-w-sm">
              {footerContent.company.description}
            </p>
            
            {/* Contact info */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  {footerContent.company.address}<br />
                  {footerContent.company.postalCode} {footerContent.company.city}
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Phone className="w-5 h-5 flex-shrink-0" />
                <a href="tel:0678269110" className="text-sm hover:text-white transition-colors">
                  {footerContent.company.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5 flex-shrink-0" />
                <a href="mailto:contact@acdc-formation.com" className="text-sm hover:text-white transition-colors">
                  {footerContent.company.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Globe className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{footerContent.company.website}</span>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Nos formations</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/ia-automatisation" className="text-gray-400 hover:text-white transition-colors text-sm">
                  IA & Automatisation
                </Link>
              </li>
              <li>
                <Link to="/management-leadership" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Management & Leadership
                </Link>
              </li>
              <li>
                <Link to="/soft-skills" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Soft Skills
                </Link>
              </li>
              <li>
                <Link to="/digital-web" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Digital & Web
                </Link>
              </li>
              <li>
                <Link to="/simulation-roi" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Simulation ROI
                </Link>
              </li>
              <li>
                <a href="/#diagnostic" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Diagnostic gratuit
                </a>
              </li>
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Informations légales</h3>
            <ul className="space-y-3">
              {footerContent.links.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${index}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social links */}
            <div className="mt-6">
              <h4 className="font-semibold text-white mb-3 text-sm">Suivez-nous</h4>
              <div className="flex gap-3">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                  aria-label="LinkedIn"
                  data-testid="footer-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                  aria-label="Twitter"
                  data-testid="footer-twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} ACDC-Formation. Tous droits réservés.
            </p>
            <p className="text-sm text-gray-500">
              Organisme de formation certifié Qualiopi
            </p>
          </div>
        </div>
      </div>

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ACDC-Formation",
            "description": "Formations et conseil en IA générative, management, soft skills et digital pour les PME, artisans et professions libérales.",
            "url": "https://acdc-formation.com",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "0678269110",
              "contactType": "customer service",
              "availableLanguage": "French"
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "7 avenue Paul Cézanne",
              "addressLocality": "Cogolin",
              "postalCode": "83600",
              "addressCountry": "FR"
            },
            "areaServed": {
              "@type": "Country",
              "name": "France"
            }
          })
        }}
      />
    </footer>
  );
};

export default Footer;
